import type { Appointment, AppointmentStatus } from "@/types/domain";

/**
 * Client-side appointment lifecycle helpers.
 *
 * The patient/doctor "Upcoming / Past / Cancelled" tabs are purely a frontend
 * view over the full appointment list returned by the backend (the list APIs
 * apply no date filter). Classifying by **status alone** is wrong: a
 * `CONFIRMED` booking whose slot is already in the past must not stay in
 * "Upcoming" with live Cancel/Join buttons. These helpers combine status with
 * the slot instant so the buckets and action buttons match reality.
 *
 * The call-window minutes mirror connect-service defaults
 * (`CALL_PRE_START_MINUTES` / `CALL_POST_END_MINUTES` in docker-compose.yml).
 * Keep them in sync so the Join button is shown exactly while connect-service
 * would actually admit the user.
 */
export const CALL_PRE_START_MINUTES = 10;
export const CALL_POST_END_MINUTES = 60;

const MINUTE_MS = 60_000;

export type AppointmentBucket = "UPCOMING" | "PAST" | "CANCELLED";

export function isActiveStatus(status: AppointmentStatus): boolean {
  return status === "PENDING" || status === "CONFIRMED";
}

export function isCancelledStatus(status: AppointmentStatus): boolean {
  return (
    status === "CANCELLED" ||
    status === "CANCELLED_BY_PATIENT" ||
    status === "CANCELLED_BY_DOCTOR" ||
    status === "CANCELLED_BY_ADMIN"
  );
}

function slotStartMs(a: Appointment): number {
  return new Date(a.slotStart).getTime();
}

function slotEndMs(a: Appointment): number {
  return new Date(a.slotEnd).getTime();
}

/** True once the call window has fully closed (slot end + post-end grace). */
export function isPastSlot(a: Appointment, now: number = Date.now()): boolean {
  const end = slotEndMs(a);
  if (Number.isNaN(end)) return false;
  return now > end + CALL_POST_END_MINUTES * MINUTE_MS;
}

/**
 * Tab/section an appointment belongs to, combining status AND slot time:
 * - cancelled statuses → "CANCELLED"
 * - active (PENDING/CONFIRMED) and slot still within its call window → "UPCOMING"
 * - everything else (active-but-expired, COMPLETED, NO_SHOW_*, RESCHEDULED) → "PAST"
 */
export function appointmentBucket(
  a: Appointment,
  now: number = Date.now(),
): AppointmentBucket {
  if (isCancelledStatus(a.status)) return "CANCELLED";
  if (isActiveStatus(a.status)) {
    return isPastSlot(a, now) ? "PAST" : "UPCOMING";
  }
  return "PAST";
}

/**
 * Whether the video room should offer a Join/Open-consult button. Matches the
 * connect-service window [slotStart - preStart, slotEnd + postEnd] so clicking
 * Join does not immediately 409.
 */
export function canJoinCall(a: Appointment, now: number = Date.now()): boolean {
  if (a.status !== "CONFIRMED") return false;
  const start = slotStartMs(a);
  const end = slotEndMs(a);
  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  const windowOpen = start - CALL_PRE_START_MINUTES * MINUTE_MS;
  const windowClose = end + CALL_POST_END_MINUTES * MINUTE_MS;
  return now >= windowOpen && now <= windowClose;
}

/**
 * Cancel / reschedule are only meaningful before the appointment starts. Once
 * the slot start has passed the consult is in progress or over (it should
 * complete or be marked no-show, not be cancelled).
 */
export function canCancelAppointment(
  a: Appointment,
  now: number = Date.now(),
): boolean {
  if (!isActiveStatus(a.status)) return false;
  const start = slotStartMs(a);
  if (Number.isNaN(start)) return false;
  return now < start;
}

/** Pay-now only makes sense for a PENDING appointment that has not lapsed. */
export function canPayAppointment(
  a: Appointment,
  now: number = Date.now(),
): boolean {
  return a.status === "PENDING" && !isPastSlot(a, now);
}
